import { CloudUpload, TrashCan } from "@carbon/icons-react";
import { FileUploaderButton } from "@carbon/react";
import { TableColumns } from "@flaminem/ui-components";
import { AxiosError } from "axios";
import { FC, useEffect, useState } from "react";

import { IconButton } from "src/components/Buttons";
import { DimensionedLayout } from "src/components/DimensionedLayout/DimensionedLayout.tsx";
import { useDimensions } from "src/components/DimensionedLayout/useDimensions.ts";
import { ModalTransition, PassiveModal } from "src/components/Modal";
import { FixedTable } from "src/components/Tables/FixedTable.tsx";
import { defaultRowHeight } from "src/config/table-config.ts";
import { mapToModalError, useMapToMutationError } from "src/errors";
import { useAsync } from "src/hooks/useAsync";
import { AttachmentConfig, AttachmentsApi } from "src/http/Attachments.api.ts";
import { useT } from "src/internationalization";
import { Attachment } from "src/types/attachment";

import { ConfirmDeleteAttachmentModal } from "./ConfirmDeleteAttachmentModal";
import { DownloadAttachmentButton } from "./DownloadAttachmentButton";

export type AttachmentsModalProps = {
  onClose: () => void;
  refreshList: () => void;
  alertId: string;
  alertNumber: string;
  attachments?: Attachment[];
};

export const AttachmentsModal: FC<AttachmentsModalProps> = ({
  onClose,
  alertId,
  alertNumber,
  attachments,
  refreshList,
}) => {
  const t = useT();
  const mapToMutationError = useMapToMutationError();
  const { pageSize, dimensions } = useDimensions();

  const [{ loading, error, errorConfig }, setUploadState] = useState<{
    loading: boolean;
    error?: AxiosError;
    errorConfig?: AxiosError;
  }>({
    loading: false,
    error: undefined,
  });

  const [config, setConfig] = useState<AttachmentConfig>();
  const [attachmentToDelete, setAttachmentToDelete] = useState<Attachment | null>(null);

  const [uploadFile, { loading: uploadFileLoading, error: uploadFileError }] = useAsync(
    (file: File) => AttachmentsApi.Attachments().uploadAttachment(alertId, file)
  );

  // TODO maybe consider using libraries like react-query to handle this
  useEffect(() => {
    const getConfig = async () => {
      try {
        setUploadState({ loading: true, error: undefined });
        const result = await AttachmentsApi.Attachments().getConfig();
        setConfig(result);
        setUploadState({ loading: false, error: undefined });
      } catch (error) {
        setUploadState({ loading: false, errorConfig: error as AxiosError });
      }
    };

    getConfig();
  }, []);

  const columns: TableColumns<Attachment> = {
    name: {
      width: 1,
      flexGrow: 1,
      flexShrink: 1,
      label: t("page.alert.modal.attachments.columns.name"),
      render: (row) => row.name,
    },
    actions: {
      width: 120,
      flexGrow: 0,
      flexShrink: 0,
      label: "",
      render: (row) => (
        <div className="flex justify-end gap-5 w-[100%]">
          <DownloadAttachmentButton attachment={row} />
          <IconButton
            kind="danger"
            size="sm"
            label={t("general.actions.delete")}
            onClick={() => setAttachmentToDelete(row)}
          >
            <TrashCan />
          </IconButton>
        </div>
      ),
    },
  };

  return (
    <>
      <PassiveModal
        size="lg"
        loading={loading || uploadFileLoading}
        error={mapToModalError(mapToMutationError(errorConfig || error || uploadFileError))}
        title={t("page.alert.modal.attachments.title")}
        subtitle={t("page.alert.modal.attachments.subtitle", { number: alertNumber })}
        onClose={onClose}
      >
        <div className="flex flex-col gap-5 h-[50vh]">
          <div>
            <span>
              {t("page.alert.modal.attachments.form.description.files", {
                extensions: config?.extensions.join(","),
              })}
            </span>
          </div>
          <div className="flex justify-end">
            <FileUploaderButton
              id="uploadFile"
              name="files"
              accept={config?.extensions}
              multiple={false}
              disabled={!!errorConfig}
              className="px-4"
              labelText={
                <div className="flex">
                  {t("page.alert.modal.attachments.form.button.files")}
                  <div className="ml-5">
                    <CloudUpload />
                  </div>
                </div>
              }
              disableLabelChanges={true}
              onChange={async (e) => {
                if (e.target?.files?.length) {
                  await uploadFile(e.target.files[0]);
                  refreshList();
                }
              }}
            />
          </div>
          <DimensionedLayout fullHeight={false} className="flex-1 min-h-0">
            {dimensions?.width && (
              <FixedTable
                id="attachments"
                data={attachments ?? []}
                columns={columns}
                isLoading={false}
                rowHeight={defaultRowHeight}
                pageSize={pageSize}
              />
            )}
          </DimensionedLayout>
        </div>
      </PassiveModal>

      <ModalTransition>
        {attachmentToDelete && (
          <ConfirmDeleteAttachmentModal
            onClose={() => setAttachmentToDelete(null)}
            onConfirm={() => {
              setAttachmentToDelete(null);
              refreshList();
            }}
            attachment={attachmentToDelete}
          />
        )}
      </ModalTransition>
    </>
  );
};
